"use client";

import { useMemo } from "react";
import {
  FormatNumber,
  Heading,
  HStack,
  Progress,
  Stack,
  Stat,
} from "@chakra-ui/react";

import {
  MdRadioButtonUnchecked,
  MdAdjust,
  MdRadioButtonChecked,
} from "react-icons/md";

function ReflectionStatItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Stat.Root>
      <HStack spaceX="2" align="center">
        {icon}
        <Stat.Label>{label}</Stat.Label>
      </HStack>
      <Stat.ValueText>
        <FormatNumber value={value} />
      </Stat.ValueText>
    </Stat.Root>
  );
}

export function ReflectionProgressBar({
  totalReflections,
  scoredReflections,
}: {
  totalReflections: number;
  scoredReflections: number;
}) {
  const progressPercentage = useMemo(() => {
    return Math.round((scoredReflections / totalReflections) * 100);
  }, [scoredReflections, totalReflections]);

  return (
    <Progress.Root defaultValue={progressPercentage} maxW="sm">
      <HStack gap="5">
        <Progress.Label>Progress</Progress.Label>
        <Progress.Track bg="moveon.primaryLight" flex="1">
          <Progress.Range bg="moveon.primary" />
        </Progress.Track>
        <Progress.ValueText>{progressPercentage}%</Progress.ValueText>
      </HStack>
    </Progress.Root>
  );
}

export function ReflectionProgressStats({
  countFullyScored,
  countUnscoredThoughts,
  countPartiallyScoredThoughts,
}: {
  countFullyScored: number;
  countUnscoredThoughts: number;
  countPartiallyScoredThoughts: number;
}) {
  // Render both data points side by side
  return (
    <>
      <Heading size="sm" mb="2">
        Thoughts distribution by reflection status
      </Heading>
      <Stack direction="row">
        <Stat.Root>
          <Stat.Label>None</Stat.Label>
          <Stat.ValueText>
            <FormatNumber value={countUnscoredThoughts} />
          </Stat.ValueText>
        </Stat.Root>
        <Stat.Root>
          <Stat.Label>Partially</Stat.Label>
          <Stat.ValueText>
            <FormatNumber value={countPartiallyScoredThoughts} />
          </Stat.ValueText>
        </Stat.Root>
        <Stat.Root>
          <Stat.Label>Fully</Stat.Label>
          <Stat.ValueText>
            <FormatNumber value={countFullyScored} />
          </Stat.ValueText>
        </Stat.Root>
      </Stack>
    </>
  );
}

export function ReflectionProgressStatsTOP({
  countFullyScored,
  countUnscoredThoughts,
  countPartiallyScoredThoughts,
}: {
  countFullyScored: number;
  countUnscoredThoughts: number;
  countPartiallyScoredThoughts: number;
}) {
  return (
    <Stack
      direction={["column", "row"]}
      // space="4"
      gap="4"
      align="stretch"
    >
      <ReflectionStatItem
        icon={<MdRadioButtonUnchecked size={20} color="gray" />}
        label="Not reflected"
        value={countUnscoredThoughts}
      />
      <ReflectionStatItem
        icon={<MdAdjust size={20} color="orange" />}
        label="Partially reflected"
        value={countPartiallyScoredThoughts}
      />
      <ReflectionStatItem
        icon={<MdRadioButtonChecked size={20} color="green" />}
        label="Fully reflected"
        value={countFullyScored}
      />
    </Stack>
  );
}
